/**
 * SwipeStack — controlled wrapper around `react-native-deck-swiper`.
 *
 * Phase 3.2 — Swipe Right (Approve) + Undo. Phase 3.3 will enable
 * left-swipe and add the reject reason bottom sheet.
 *
 * Why deck-swiper (per plan §15): battle-tested swipe stack with
 * spring physics + worklet-style animations, ~50KB, 60fps on mid-
 * range Android. Alternative (custom Reanimated) was considered but
 * the visual spec (3-card stack with peeks, drag overlay, commit
 * spring) is exactly what deck-swiper provides out of the box.
 *
 * Controlled mode: parent owns `cardIndex` and the `cards` array.
 * We never mutate `cards` ourselves; the parent handles swipe /
 * undo state and either re-orders or replaces the array.
 *
 * Imperative surface (via `ref`):
 *  - swipeRight() — driven by a11y "Setuju" button
 *  - swipeBack()  — driven by undo button
 *  - jumpToCardIndex(idx) — driven by undo (rewind)
 */

import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import Swiper from 'react-native-deck-swiper';

import { StackCard } from './StackCard';
import { AppIcon } from '@/components/app-icon';
import { Brand, Spacing } from '@/constants/theme';
import { MISC_ICON } from '@/lib/icons';
import { shouldResetDeckAfterCardAppend } from '@/lib/swipe-stack-recovery';
import type { WorksheetCard } from '@/types/daily';

export type SwipeStackHandle = {
  /** Programmatically swipe the top card right (Setuju). */
  swipeRight: () => void;
  /** Programmatically swipe the top card left (Ganti). */
  swipeLeft: () => void;
  /** Animate the previous card back to top — undo. */
  swipeBack: () => void;
  /** Snap to a specific card index (used by undo to rewind). */
  jumpToCardIndex: (index: number) => void;
};

export type SwipeStackProps = {
  cards: WorksheetCard[];
  cardIndex: number;
  childName: string;
  /** Fires when a right-swipe commits (top card, animation done). */
  onSwipedRight: (cardIndex: number) => void;
  /** Fires when a left-swipe commits. Parent logs the reject immediately
   *  and shows the optional RejectReasonBanner (Phase 3.6). */
  onSwipedLeft?: (cardIndex: number) => void;
  /** Fires when a tap on the top card is detected (Phase 4 uses this). */
  onTapCard?: (cardIndex: number) => void;
  /** Fires when the last card is swiped off. */
  onSwipedAll?: () => void;
  /** Block the deck and show instant feedback while a swipe is persisted. */
  loading?: boolean;
  loadingLabel?: string;
  /**
   * Optional gate to disable left-swipe (default: enabled). Currently
   * unused — Phase 3.6 removed the swap cap so rejects are always
   * allowed; kept as an escape hatch for future gating.
   */
  leftSwipeEnabled?: boolean;
};

export const SwipeStack = forwardRef<SwipeStackHandle, SwipeStackProps>(
  function SwipeStack(
    {
      cards,
      cardIndex,
      childName,
      onSwipedRight,
      onSwipedLeft,
      onTapCard,
      onSwipedAll,
      leftSwipeEnabled = true,
      loading = false,
      loadingLabel = 'Menyimpan…',
    },
    ref,
  ) {
    const swiperRef = useRef<React.ComponentRef<typeof Swiper<WorksheetCard>> | null>(null);
    const previousCardsLengthRef = useRef(cards.length);

    useImperativeHandle(
      ref,
      () => ({
        swipeRight: () => swiperRef.current?.swipeRight(),
        swipeLeft: () => swiperRef.current?.swipeLeft(),
        swipeBack: () => swiperRef.current?.swipeBack(),
        jumpToCardIndex: (idx: number) => swiperRef.current?.jumpToCardIndex(idx),
      }),
      [],
    );

    useEffect(() => {
      const shouldReset = shouldResetDeckAfterCardAppend(
        previousCardsLengthRef.current,
        cards.length,
        cardIndex,
      );
      previousCardsLengthRef.current = cards.length;
      if (shouldReset) {
        swiperRef.current?.jumpToCardIndex(cardIndex);
      }
    }, [cards.length, cardIndex]);

    return (
      <View style={styles.frame}>
        <Swiper
          ref={swiperRef}
          cards={cards}
          cardIndex={cardIndex}
          keyExtractor={(card) => String(card.worksheetId)}
          renderCard={(card, renderedIndex) =>
            card ? (
              <StackCard
                card={card}
                variant={renderedIndex === cardIndex ? 'top' : 'peek'}
                showPeekTitle={renderedIndex === cardIndex + 1}
                childName={childName}
              />
            ) : null
          }
          stackSize={3}
          stackSeparation={42}
          stackScale={5}
          // Y1: right-swipe (approve) + left-swipe (reject + bottom sheet) in
          // 3.3. Top/bottom swipes are Y2 (Lihat nanti / Lewati).
          disableLeftSwipe={!leftSwipeEnabled}
          disableTopSwipe
          disableBottomSwipe
          verticalSwipe={false}
          // Spring physics per plan §14
          stackAnimationFriction={6}
          stackAnimationTension={80}
          // Drag visual feedback
          animateOverlayLabelsOpacity
          overlayLabels={{
            left: {
              title: 'GANTI',
              style: {
                label: {
                  // Brand.accent orange — ganti ≠ aksi berbahaya (Fase 4).
                  backgroundColor: 'rgba(255, 138, 61, 0.9)',
                  color: '#ffffff',
                  fontSize: 28,
                  fontWeight: '700',
                  padding: 12,
                  borderRadius: 12,
                },
                wrapper: {
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  justifyContent: 'flex-start',
                  marginTop: 60,
                  marginLeft: -40,
                },
              },
            },
            right: {
              title: 'SETUJU',
              style: {
                label: {
                  // Brand.teal — positif, sesuai style guide tombol-hijau.
                  backgroundColor: 'rgba(10, 116, 115, 0.9)',
                  color: '#ffffff',
                  fontSize: 28,
                  fontWeight: '700',
                  padding: 12,
                  borderRadius: 12,
                },
                wrapper: {
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  marginTop: 60,
                  marginLeft: 40,
                },
              },
            },
          }}
          onSwipedRight={(idx) => onSwipedRight(idx)}
          onSwipedLeft={(idx) => onSwipedLeft?.(idx)}
          onTapCard={(idx) => onTapCard?.(idx)}
          onSwipedAll={() => onSwipedAll?.()}
          backgroundColor="transparent"
          containerStyle={styles.swiperContainer}
        />
        {/* Empty state — deck habis (semua kartu sudah di-swipe) atau memang
            tidak ada kartu. Kasus "kuota 5 approve tercapai" ditangani di
            home.tsx dengan pesan yang lebih spesifik. */}
        {cards.length === 0 || cardIndex >= cards.length ? (
          <View style={styles.emptyHint} pointerEvents="none">
            <AppIcon source={MISC_ICON.celebration} size={72} />
            <Text style={styles.emptyHintText}>
              Semua rekomendasi worksheet hari ini sudah dilihat.
            </Text>
            <Text style={styles.emptyHintSub}>
              Kembali lagi besok untuk rekomendasi baru.
            </Text>
          </View>
        ) : null}
        {loading ? (
          <View
            pointerEvents="box-only"
            style={styles.loadingOverlay}
            accessibilityRole="progressbar"
            accessibilityLabel={loadingLabel}
          >
            <ActivityIndicator size="large" color={Brand.primary} />
            <Text style={styles.loadingText}>{loadingLabel}</Text>
          </View>
        ) : null}
      </View>
    );
  },
);

const styles = StyleSheet.create({
  frame: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  swiperContainer: {
    flex: 1,
    width: '100%',
    backgroundColor: 'transparent',
  },
  emptyHint: {
    position: 'absolute',
    alignItems: 'center',
    gap: 12,
  },
  emptyHintText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#201c23',
    textAlign: 'center',
    maxWidth: 280,
  },
  emptyHintSub: {
    fontSize: 14,
    color: '#6b6573',
    textAlign: 'center',
    maxWidth: 280,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 10,
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.two,
    backgroundColor: 'rgba(252, 251, 248, 0.88)',
  },
  loadingText: {
    color: Brand.primary,
    fontSize: 13,
    fontWeight: '700',
  },
});
